/**
 * Audit Logs Page
 *
 * Comprehensive audit trail of all user actions and system events.
 * Matches the consolidated pattern of SyncHistory page.
 */

import React, { useState, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Grid,
  Autocomplete,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material'
import {
  Download as DownloadIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  auditApi,
  AuditLog,
  AuditFilters,
  AuditLogListResponse,
  AuditStats,
  AuditEventTypesResponse,
  AuditEventTypeOption
} from '../../../services/auditApi'
import {
  buildLabelMap,
  buildCategoryMap,
  rowFlagged,
  SEVERITY_LABELS,
} from './auditLabels'
import AdminPageHeader from '../../../components/admin/shared/AdminPageHeader'
import BaseDataTable from '../../../components/shared/BaseDataTable'
import ReportFilterCard from '../../../components/shared/filters/ReportFilterCard'
import { FilterKey } from '../../../components/shared/filters/types'
import { useReportFilters } from '../../../components/shared/filters'
import { useDataFetching } from '../../../hooks/useDataFetching'
import { poolwerxColors } from '../../../theme/colors'
import {
  AuditLogDrawer,
  createAuditLogColumns
} from './components'
import { MISMATCH_ROW_BG } from '../../../theme/exceptionTableStyles'

const ENABLED_FILTERS: FilterKey[] = ['textSearch', 'dateRange']

const AuditLogs: React.FC = () => {
  // UI State
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  // Pagination
  const [page, setPage] = useState(1)
  const [perPage] = useState(25)

  // Shared filter hook with URL persistence
  const {
    filters,
    updateFilter,
    clearFilters: clearSharedFilters,
    hasActiveFilters: hasSharedActiveFilters,
  } = useReportFilters({
    enabledFilters: ENABLED_FILTERS,
    syncToUrl: true,
  })

  // Custom audit-specific filters (not in shared types)
  // Seed from URL params so deep-links + bookmarks restore state.
  const [searchParams, setSearchParams] = useSearchParams()
  const [eventTypeFilter, setEventTypeFilter] = useState<string>(
    () => searchParams.get('event_type') || ''
  )
  const [eventCategoryFilter, setEventCategoryFilter] = useState<string>(
    () => searchParams.get('event_category') || ''
  )
  const [successFilter, setSuccessFilter] = useState<string>(
    () => searchParams.get('success') || ''
  )
  const [severityFilter, setSeverityFilter] = useState<string>(
    () => searchParams.get('severity') || ''
  )
  // System-maintenance events are internal noise - hidden unless toggled on.
  const [includeSystem, setIncludeSystem] = useState<boolean>(
    () => searchParams.get('include_system') === 'true'
  )

  // Write an audit-specific filter to the URL so filters are bookmarkable.
  // useReportFilters owns textSearch/date keys; these keys are disjoint, so the
  // functional merge below never clobbers the shared hook's params.
  const syncParam = useCallback((key: string, value: string | boolean) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      const v = typeof value === 'boolean' ? (value ? 'true' : '') : value
      if (v) next.set(key, v)
      else next.delete(key)
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setEventType = useCallback((v: string) => { setEventTypeFilter(v); syncParam('event_type', v); setPage(1) }, [syncParam])
  const setCategory = useCallback((v: string) => { setEventCategoryFilter(v); syncParam('event_category', v); setPage(1) }, [syncParam])
  const setSuccess = useCallback((v: string) => { setSuccessFilter(v); syncParam('success', v); setPage(1) }, [syncParam])
  const setSeverity = useCallback((v: string) => { setSeverityFilter(v); syncParam('severity', v); setPage(1) }, [syncParam])
  const setSystem = useCallback((v: boolean) => { setIncludeSystem(v); syncParam('include_system', v); setPage(1) }, [syncParam])

  // Check if any filters are active
  const hasActiveFilters =
    hasSharedActiveFilters
    || Boolean(eventTypeFilter)
    || Boolean(eventCategoryFilter)
    || Boolean(successFilter)
    || Boolean(severityFilter)
    || includeSystem

  // Convert to API filters
  const apiFilters = useMemo((): AuditFilters => {
    const result: AuditFilters = {
      page: page,
      page_size: perPage
    }

    // Prominent free-text search - the backend spans actor/FP/target/action/event.
    if (filters.textSearch) result.search = filters.textSearch
    if (filters.startDate) result.start_date = filters.startDate + 'T00:00:00'
    if (filters.endDate) result.end_date = filters.endDate + 'T23:59:59'
    if (eventTypeFilter) result.event_type = eventTypeFilter
    if (eventCategoryFilter) result.event_category = eventCategoryFilter
    if (successFilter) result.success = successFilter
    if (severityFilter) result.severity = severityFilter
    if (includeSystem) result.include_system = true

    return result
  }, [filters, eventTypeFilter, eventCategoryFilter, successFilter, severityFilter, includeSystem, page, perPage])

  // Fetch audit stats
  const { data: stats } = useDataFetching<AuditStats>({
    fetchFn: async () => auditApi.getStats(),
    dependencies: []
  })

  // Fetch event types
  const { data: eventTypes } = useDataFetching<AuditEventTypesResponse>({
    fetchFn: async () => auditApi.getEventTypes(),
    dependencies: []
  })

  // Fetch audit logs
  const {
    data: logsData,
    loading,
    error
  } = useDataFetching<AuditLogListResponse>({
    fetchFn: async () => auditApi.getLogs(apiFilters),
    dependencies: [apiFilters]
  })

  // Derived data
  const logs = logsData?.logs || []
  const total = logsData?.total || 0

  // Event handlers
  const handleFilterChange = (key: string, value: string) => {
    updateFilter(key as keyof typeof filters, value)
    setPage(1)
  }

  const handleClearFilters = () => {
    clearSharedFilters()
    setEventType('')
    setCategory('')
    setSuccess('')
    setSeverity('')
    setSystem(false)
    setPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleViewDetails = useCallback((log: AuditLog) => {
    setSelectedLog(log)
    setDetailsOpen(true)
  }, [])

  const exportExcel = async () => {
    try {
      setExportError(null)
      await auditApi.exportLogsExcel(apiFilters)
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Excel export failed'
      console.error('Excel export error:', err)
      setExportError('Failed to export audit logs: ' + errorMessage)
    }
  }

  // value -> label / category maps from the registry payload (no duplicated
  // label table; the table cell falls back to humanise for unknown values).
  const labelMap = useMemo(
    () => buildLabelMap(eventTypes?.event_types),
    [eventTypes]
  )
  const categoryMap = useMemo(
    () => buildCategoryMap(eventTypes?.event_types),
    [eventTypes]
  )

  // Create table columns
  const columns = useMemo(
    () => createAuditLogColumns(handleViewDetails, labelMap, categoryMap),
    [handleViewDetails, labelMap, categoryMap]
  )

  // Selected event-type option object for the controlled Autocomplete.
  const selectedEventType = useMemo(
    () => eventTypes?.event_types.find(o => o.value === eventTypeFilter) || null,
    [eventTypes, eventTypeFilter]
  )

  // Quick-filter chips: each sets a known filter combination in one click.
  const applyQuickChip = useCallback((key: string) => {
    setPage(1)
    if (key === 'failed') {
      setSuccess(successFilter === 'false' ? '' : 'false')
    } else if (key === 'access') {
      setCategory(eventCategoryFilter === 'ACCESS_CONTROL' ? '' : 'ACCESS_CONTROL')
    } else if (key === 'admin') {
      setCategory(eventCategoryFilter === 'ADMINISTRATION' ? '' : 'ADMINISTRATION')
    } else if (key === 'system') {
      setSystem(!includeSystem)
    } else if (key === 'today') {
      const today = format(new Date(), 'yyyy-MM-dd')
      updateFilter('startDate', today)
      updateFilter('endDate', today)
    } else if (key === 'last7') {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 7)
      updateFilter('startDate', format(start, 'yyyy-MM-dd'))
      updateFilter('endDate', format(end, 'yyyy-MM-dd'))
    }
  }, [updateFilter, setSuccess, setCategory, setSystem, successFilter, eventCategoryFilter, includeSystem])

  // Combine errors
  const displayError = error || exportError

  return (
    <>
      <AdminPageHeader pageKey="auditLogs" />

      {displayError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setExportError(null)}>
          {displayError}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <HistoryIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Total Events
                  </Typography>
                  <Typography variant="h5">
                    {stats.total_events.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <SecurityIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Today's Events
                  </Typography>
                  <Typography variant="h5" color="primary">
                    {stats.events_today.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <ErrorIcon sx={{ fontSize: 32, color: 'error.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Failed Events
                  </Typography>
                  <Typography variant="h5" color="error">
                    {stats.failed_events.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <SuccessIcon sx={{ fontSize: 32, color: 'success.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Active Users
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {stats.unique_users}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <ReportFilterCard
        filters={filters}
        enabledFilters={ENABLED_FILTERS}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
        loading={loading}
        exportButton={
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportExcel}
            size="small"
            sx={{
              borderColor: '#E6E8EA',
              color: '#1F2A2E',
              '&:hover': { borderColor: poolwerxColors.teal, backgroundColor: '#E6F4F5' }
            }}
          >
            Export Excel
          </Button>
        }
        extraFilters={
          <Stack spacing={1.5} sx={{ width: '100%' }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
              <Autocomplete<AuditEventTypeOption>
                size="small"
                sx={{ minWidth: 240 }}
                options={eventTypes?.event_types || []}
                groupBy={(o) => o.category}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                value={selectedEventType}
                onChange={(_, option) => setEventType(option?.value || '')}
                renderInput={(params) => <TextField {...params} label="Event Type" />}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={severityFilter}
                  label="Severity"
                  onChange={(e) => setSeverity(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {(eventTypes?.severities || []).map(s => (
                    <MenuItem key={s} value={s}>{SEVERITY_LABELS[s] || s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Result</InputLabel>
                <Select
                  value={successFilter}
                  label="Result"
                  onChange={(e) => setSuccess(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Success</MenuItem>
                  <MenuItem value="false">Failed</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeSystem}
                    onChange={(e) => setSystem(e.target.checked)}
                  />
                }
                label="Include system events"
              />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Quick:
              </Typography>
              <Chip
                label="Failed only"
                size="small"
                color={successFilter === 'false' ? 'error' : 'default'}
                variant={successFilter === 'false' ? 'filled' : 'outlined'}
                onClick={() => applyQuickChip('failed')}
              />
              <Chip
                label="Security/access"
                size="small"
                color={eventCategoryFilter === 'ACCESS_CONTROL' ? 'primary' : 'default'}
                variant={eventCategoryFilter === 'ACCESS_CONTROL' ? 'filled' : 'outlined'}
                onClick={() => applyQuickChip('access')}
              />
              <Chip
                label="Admin changes"
                size="small"
                color={eventCategoryFilter === 'ADMINISTRATION' ? 'primary' : 'default'}
                variant={eventCategoryFilter === 'ADMINISTRATION' ? 'filled' : 'outlined'}
                onClick={() => applyQuickChip('admin')}
              />
              <Chip
                label="System events"
                size="small"
                color={includeSystem ? 'primary' : 'default'}
                variant={includeSystem ? 'filled' : 'outlined'}
                onClick={() => applyQuickChip('system')}
              />
              <Chip label="Today" size="small" variant="outlined" onClick={() => applyQuickChip('today')} />
              <Chip label="Last 7 days" size="small" variant="outlined" onClick={() => applyQuickChip('last7')} />
            </Stack>
          </Stack>
        }
      />

      {/* Count line - filtered total vs the page currently loaded (the stat
          cards above are global totals, independent of these filters). */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {total > 0
          ? `Showing ${logs.length} of ${total.toLocaleString()} ${hasActiveFilters ? 'matching' : 'total'} events`
          : 'No events to show'}
      </Typography>

      {/* Audit Logs Table */}
      <BaseDataTable
        variant="enhanced"
        title="Audit Events"
        titleIcon={<SecurityIcon />}
        columns={columns}
        data={logs}
        loading={loading}
        emptyMessage={
          hasActiveFilters
            ? 'No audit logs match your current filters'
            : 'No audit logs found'
        }
        pagination={{
          page: page,
          perPage: perPage,
          total: total,
          onPageChange: handlePageChange,
          showFirstLast: true,
          size: 'large'
        }}
        getRowKey={(log) => log.id}
        getRowSx={(log) => (rowFlagged(log) ? { backgroundColor: MISMATCH_ROW_BG } : {})}
      />

      {/* Log Details Drawer */}
      <AuditLogDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        log={selectedLog}
        labelMap={labelMap}
      />
    </>
  )
}

export default AuditLogs
